import { InstitutionDashboardState, JourneysDashboardState } from 'redux/schemas/models/institution';
import { normalize } from 'normalizr';
import { institutionSchema } from 'redux/schemas/api/institutions';
import {
  getCourseCounts,
  getCourseClonings,
  getInstitutionData,
  setMyViewData,
  getJourneyClonings,
  setAutomatedTranslationState,
  setCourseHighlight,
  setSpeechToText,
  getJourneyCounts,
  finishCourseCloning,
  finishJourneyCloning,
} from 'redux/actions/institutions';
import { setAiRolePlayCourseAccessSetting } from 'redux/actions/ai-roleplay-usage';
import { createReducer } from '@reduxjs/toolkit';
import { initialRootState, newState } from '.';

export const initialInstitutionDashboardState: InstitutionDashboardState = {
  courseTypeCounts: {
    myView: 0,
    active: 0,
    demo: 0,
    future: 0,
    past: 0,
    total: 0,
    primary: 0,
  },
  countsLoaded: false,
  myViewData: null,
  courseHighlight: null,
};

export const initialJourneysDashboardState: JourneysDashboardState = {
  journeyTypeCounts: {
    myView: 0,
    active: 0,
    demo: 0,
    future: 0,
    past: 0,
    total: 0,
    primary: 0,
  },
  countsLoaded: false,
};

export default createReducer(initialRootState, builder => {
  builder.addCase(getCourseCounts.fulfilled, (state, action) => {
    state.app.institutionDashboard.courseTypeCounts = action.payload;
    state.app.institutionDashboard.countsLoaded = true;
  });
  builder.addCase(getJourneyCounts.fulfilled, (state, action) => {
    state.app.journeysDashboard.journeyTypeCounts = action.payload;
    state.app.journeysDashboard.countsLoaded = true;
  });
  builder.addCase(getCourseClonings.fulfilled, (state, action) => {
    state.models.courseClonings = action.payload;
  });
  builder.addCase(finishCourseCloning.fulfilled, (state, action) => {
    const courseIndex = state.models.courseClonings?.findIndex((clone) => clone.id === action.meta.arg.courseId);
    const course = state.models.courseClonings[courseIndex];
    const parent = state.models.courses[course.owner.catalogId];
    if (typeof courseIndex === 'number' && courseIndex > -1) {
      state.models.courseClonings.splice(courseIndex, 1);
    }
    const { originalCourseCounts } = action.meta.arg;

    if (parent.isPrimary
      && (action.payload.primary - originalCourseCounts.primary) === 0) {
      // It means the new clone is a cohort, because the count of primary courses hasn't change
      parent.numCohorts += 1;
    }
    state.app.differenceInCourseCounts = {
      active: (action.payload.active - originalCourseCounts.active),
      future: (action.payload.future - originalCourseCounts.future),
      past: (action.payload.past - originalCourseCounts.past),
      myView: (action.payload.myView - originalCourseCounts.myView),
      demo: (action.payload.demo - originalCourseCounts.demo),
      primary: (action.payload.primary - originalCourseCounts.primary),
      total: (action.payload.total - originalCourseCounts.total),
    };
  });
  builder.addCase(getJourneyClonings.fulfilled, (state, action) => {
    state.models.journeyClonings = action.payload;
  });
  builder.addCase(finishJourneyCloning.fulfilled, (state, action) => {
    const journeyIndex = state.models.journeyClonings?.findIndex((clone) => clone.settingsHash.catalogId === action.meta.arg.catalogId);
    if (typeof journeyIndex === 'number' && journeyIndex > -1) {
      state.models.journeyClonings.splice(journeyIndex, 1);
    }
    const { originalJourneyCounts } = action.meta.arg;

    state.app.differenceInJourneyCounts = {
      active: (action.payload.active - originalJourneyCounts.active),
      future: (action.payload.future - originalJourneyCounts.future),
      past: (action.payload.past - originalJourneyCounts.past),
      total: (action.payload.total - originalJourneyCounts.total),
      myView: 0,
      demo: 0,
      primary: 0,
    };
  });
  builder.addCase(getInstitutionData.fulfilled, (state, action) => {
    const { result: requestResult } = action.payload;
    const normalized = normalize(requestResult, institutionSchema);
    state.models.institutions[normalized.result] = normalized.entities.institutions[normalized.result] as any;
  });
  builder.addCase(setMyViewData, (state, action) => {
    state.app.institutionDashboard.myViewData = action.payload;
  });
  builder.addCase(setAutomatedTranslationState.fulfilled, (state, action) => {
    state.models.institutions[state.app.currentInstitutionId].automatedTranslationEnabled = action.meta.arg.enabled;
  });
  builder.addCase(setSpeechToText.fulfilled, (state, action) => {
    state.models.institutions[state.app.currentInstitutionId].speechToTextEnabled = action.meta.arg.enabled;
  });
  builder.addCase(setAiRolePlayCourseAccessSetting.fulfilled, (state, action) => {
    state.models.institutions[state.app.currentInstitutionId].aiRolePlayCourseAccessSetting = action.meta.arg.setting;
  });
  builder.addCase(setCourseHighlight, (state, action) => {
    state.app.institutionDashboard.courseHighlight = action.payload;
  });
});
